import classNames from "classnames";
import { Clock } from "lucide-react";
import type { ReactNode } from "react";

import type { FilterDefinition } from "./FilterDefinition";
import { type FilterComparator, presetsByType } from "./FilterSchema";
import styles from "./OperatorPicker.module.css";
import { comparatorLabel, spokenComparatorLabel } from "./operators";

interface OperatorPickerProps {
  fieldType: FilterDefinition["type"];
  value: FilterComparator;
  onChange: (next: FilterComparator) => void;
  /**
   * The comparators to render as pills — the field's effective set (see
   * `effectiveComparators`). Presets (e.g. date relatives) are not included
   * here; the picker layers them on separately from `presetsByType`.
   */
  operators: readonly FilterComparator[];
}

/**
 * Pill-shaped row of operator buttons. Renders the `operators` it's given
 * (the field's effective comparator set) plus any presets for the type,
 * labeling each via `comparatorLabel`; the selected pill is filled, the
 * rest are quiet outlines. Numeric symbol labels (=, ≠, ≤, …) get a spoken
 * `aria-label` so screen readers announce them deterministically.
 */
export function OperatorPicker({
  fieldType,
  value,
  onChange,
  operators,
}: OperatorPickerProps) {
  const presets = presetsByType[fieldType];

  return (
    <div className={styles.picker} role="radiogroup" aria-label="Operator">
      <div className={styles.row}>
        {operators.map((operator) => (
          <Pill
            key={operator}
            operator={operator}
            fieldType={fieldType}
            isSelected={operator === value}
            onSelect={() => onChange(operator)}
          />
        ))}
      </div>
      {presets.length > 0 ? (
        <div className={styles.row}>
          {presets.map((operator) => (
            <Pill
              key={operator}
              operator={operator}
              fieldType={fieldType}
              isSelected={operator === value}
              onSelect={() => onChange(operator)}
              className={styles.pillPreset}
              icon={<Clock className={styles.presetIcon} aria-hidden="true" />}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface PillProps {
  operator: FilterComparator;
  fieldType: FilterDefinition["type"];
  isSelected: boolean;
  onSelect: () => void;
  /** Caller-applied class composed with the base pill / selected styles. */
  className?: string;
  /** Optional leading icon rendered before the label (e.g. presets show a clock). */
  icon?: ReactNode;
}

function Pill({
  operator,
  fieldType,
  isSelected,
  onSelect,
  className,
  icon,
}: PillProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      // Numeric pills render as symbols (=, ≠, ≤, …) which screen
      // readers narrate inconsistently. Always provide an explicit
      // phrase form so SR users get deterministic announcement.
      aria-label={spokenComparatorLabel(fieldType, operator)}
      className={classNames(
        styles.pill,
        { [styles.pillSelected]: isSelected },
        className,
      )}
      onClick={onSelect}
    >
      {icon}
      {comparatorLabel(fieldType, operator)}
    </button>
  );
}
